/**
 * Generates a unit test file in the style of missingStatus.test.js
 * based on session interactions
 */
export const generateUnitTest = (session: any, release: any, testName: string): string => {
    if (!session || !session.interactions || session.interactions.length === 0) {
        throw new Error("Session must have at least one interaction");
    }

    // Sort interactions by created date
    const sortedInteractions = [...session.interactions].sort((a, b) => {
        return new Date(a.created).getTime() - new Date(b.created).getTime();
    });

    // Filter to only include interactions with data (exclude task mode without data)
    const dataInteractions = sortedInteractions.filter((interaction) => {
        return interaction.data && Object.keys(interaction.data).length > 0;
    });

    if (dataInteractions.length === 0) {
        throw new Error("Session must have at least one interaction with data");
    }

    const testDisplayName = `Session ${session.id.substring(0, 8)}`;
    const releaseData = session.graph || {};

    // for some reason vite will treat this string as an actual import path
    // we need to do some funky stuff
    const requireString = (path: string) => `req${"uire"}("${path}")`;

    // Generate the test file content
    let testContent = `const { resetMockData } = ${requireString("@imminently/imminently_fn")};
const { filter } = ${requireString("lodash")};
const deepmerge = ${requireString("deepmerge")};
const { service } = ${requireString("../func")};
const data = ${requireString(`./${testName}.json`)};
const { ensureParsedGraph } = ${requireString("@imminently/decisively-core")};

describe("${testDisplayName}", () => {
  const originalConsoleLog = console.log;
  const originalConsoleError = console.error;
  /*
   *  ██████╗  ███╗   ██╗████████╗██╗ ██████╗ ███╗   ██╗
   *  ██╔═══╝ ██╔══██╗██║   ██║╚══██╔══╝██║██╔═══██╗████╗  ██║
   *  ██║     ███████║██║   ██║   ██║   ██║██║   ██║██╔██╗ ██║
   *  ██║     ██╔══██║██║   ██║   ██║   ██║██║   ██║██║╚██╗██║
   *  ██████╗ ██║  ██║╚██████╔╝   ██║   ██║╚██████╔╝██║ ╚████║
   *  ╚═════╝ ╚═╝  ╚═╝ ╚═════╝    ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝
   *
   *  WARNING: Console logging is mocked in these tests!
   *  If you're debugging, comment out the console.log and console.error
   *  mocking in beforeEach() and afterEach() to see actual output.
   */
  const ENABLE_LOGGING = process.env.LOGGING === "true";
  beforeEach(() => {
    // Mock console methods to avoid cluttering test output
    if (!ENABLE_LOGGING) {
      console.log = jest.fn();
      console.error = jest.fn();
    }
  });

  afterEach(() => {
    // Restore console methods
    if (!ENABLE_LOGGING) {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
    }
  });

  it("1. ", async () => {
    resetMockData({
      releases: [
        {
          ...data,
          id: "${release?.id || 1}",
          model: "${release?.model || 1}",
          env: "test",
          lastModified: Date.now(),
          activeVersionFlag: true,
        },
      ],
    });

    const sessionCreateResponse = await service.test(
      "session",
      {
        data: ${JSON.stringify(dataInteractions[0].data, null, 10)},
        mode: "${dataInteractions[0].mode || "api"}",
        goal: "${session.goal || ""}",
      },
      {
        query: {
          id: data.model,
        },
        action: "session",
        method: "POST",
      },
    );

    //console.log(sessionCreateResponse);
`;

    // Add subsequent interactions as update blocks
    for (let i = 1; i < dataInteractions.length; i++) {
        const interaction = dataInteractions[i];
        testContent += `
    {
      const sessionUpdateResponse = await service.test(
        "session",
        {
          mode: "${interaction.mode || "api"}",
          data: ${JSON.stringify(interaction.data, null, 10)},
        },
        {
          query: {
            id: data.model,
            session: sessionCreateResponse.sessionId,
            interaction: sessionCreateResponse.interactionId,
          },
          action: "session",
          method: "POST",
        },
      );

      expect(sessionUpdateResponse.sessionId).toEqual(sessionCreateResponse.sessionId);
    }
`;
    }

    testContent += `
    //console.log(sessionUpdateResponse);

    //expect(response.result).toBeDefined();
  });
});
`;

    return testContent;
};

/**
 * Downloads the generated unit test as a .js file
 */
export const downloadUnitTest = (session: any, release: any, testName: string): void => {
    try {
        const testContent = generateUnitTest(session, release, testName);
        const fileName = `${testName}.test.js`;

        const blob = new Blob([testContent], { type: "text/javascript" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Failed to generate unit test:", error);
        throw error;
    }
};
